import { ButtonProps } from '@material-ui/core';
import { Payment, Receipt } from '@material-ui/icons';
import { MaterialTableProps } from 'material-table';
import React, { Suspense, useEffect } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import Layout from '../../../../components/Layout/Layout';
import { formatNumber } from '../../../../helpers/formatNumber';
import { tFormKey, tKey } from '../../../../helpers/translate';
import { markInvoiceAsPaid, payInvoices } from '../../../../redux/accounting/actions';
import { InvoiceListItem } from '../../../../redux/accounting/definitions';
import { selectLoading } from '../../../../redux/accounting/selectors';
import { showSnackbar } from '../../../../redux/FeedbackAPI/actions';
import { SeveritySnackbar } from '../../../../redux/FeedbackAPI/definitions';
import { selectUserCan } from '../../../../redux/permissions/selectors';
import { AppState } from '../../../../redux/root-reducer';
import StateCode from '../../../../types/Invoices/StateCode';
import EntityMenuRight from '../../../Entities/EntityMenuRight/EntityMenuRight';
import AccountingSubmenu from '../../AccountingSubmenu';
import useInvoiceList from '../hooks/useInvoiceList';
import InvoicesList from './InvoicesList';

const TransitionModal = React.lazy(() => import('../../../../components/Modal/TransitionModal'));

interface AuthorizedInvoicesListProps extends RouteComponentProps<{ id: string }> {
  accountingLoading: boolean;
  can_pay: boolean;
  can_mark_as_paid: boolean;
  payInvoices: () => void;
  markAsPaid: (invoice_id: number) => void;
  rollbackSentInvoice: (invoice_id: number, stateCode: StateCode) => void;
  showSnackbar: (message: string, severity: SeveritySnackbar) => void;
}

const AuthorizedInvoicesList: React.FC<AuthorizedInvoicesListProps> = ({
  accountingLoading,
  can_pay,
  can_mark_as_paid,
  payInvoices,
  markAsPaid,
  showSnackbar,
}) => {
  const { loading, invoices, errorListMessage } = useInvoiceList('authorized', accountingLoading);
  const [authorizeModal, setAuthorizeModal] = React.useState({ view: false, invoice_id: 0, text: '' });

  useEffect(() => {
    if (errorListMessage) {
      showSnackbar(errorListMessage, 'error');
    }
  }, [errorListMessage, showSnackbar]);

  const actionIcons = {
    ForcePay: () => <Receipt style={{ color: 'rgba(0, 0, 0, 0.54)' }} />,
  };

  const actions = [];

  if (can_mark_as_paid) {
    actions.push((rowData: InvoiceListItem) => ({
      icon: actionIcons.ForcePay,
      tooltip: tFormKey('Marcar como pagada'),
      onClick: () => setAuthorizeModal({ view: true, invoice_id: rowData.id, text: rowData.entity_name }),
      hidden: rowData.state_code !== StateCode.AUTORIZADA,
    }));
  }

  const tableData: MaterialTableProps<InvoiceListItem> = {
    title: tFormKey('authorizedInvoicesList.title'),
    columns: [
      {
        field: 'reference_number',
        title: tKey('Factura'),
        //@ts-ignore
        width: '120px',
      },
      {
        field: 'entity_name',
        title: tKey('Proveedor'),
        //@ts-ignore
        width: '200px',
      },
      {
        field: 'invoice_date',
        title: tFormKey('Fecha'),
        headerStyle: {
          textAlign: 'center',
        },
        cellStyle: {
          textAlign: 'center',
        },
        render: (rowData: InvoiceListItem) => <div style={{ textAlign: 'center' }}>{rowData.invoice_date}</div>,
        //@ts-ignore
        width: '120px',
      },
      {
        field: 'due_date',
        title: tFormKey('Vencimiento'),
        headerStyle: {
          textAlign: 'center',
        },
        cellStyle: {
          textAlign: 'center',
        },
        render: (rowData: InvoiceListItem) => <div style={{ textAlign: 'center' }}>{rowData.due_date}</div>,
        //@ts-ignore
        width: '120px',
      },
      {
        field: 'total_amount',
        title: tKey('Total'),
        type: 'numeric',
        render: (rowData: InvoiceListItem) => <div>{formatNumber({ value: rowData.total_amount }) + ' €'}</div>,
        //@ts-ignore
        width: '100px',
      },
      {
        field: 'center_costs',
        title: tKey('Centro de coste'),
        //@ts-ignore
        width: '200px',
      },
      {
        field: 'state',
        title: tKey('Estado'),
        //@ts-ignore
        width: '100px',
      },
    ],
    data: invoices,
    actions,
    options: {
      tableLayout: 'auto',
      pageSize: 100,
      pageSizeOptions: [20, 50, 100],
    },
  };
  tableData.isLoading = loading || accountingLoading;

  const button: ButtonProps[] = [
    {
      children: tKey('Generar fichero'),
      onClick: payInvoices,
      startIcon: <Payment />,
      fullWidth: true,
      variant: 'contained',
      color: 'primary',
      hidden: !can_pay,
    },
  ];

  return (
    <Layout
      leftSubmenu={<AccountingSubmenu selected="invoice" submenu={tKey('Pagos')} />}
      rightSubmenu={<EntityMenuRight actionButtons={button} />}
    >
      <InvoicesList tableData={tableData} downloadAction />
      <Suspense fallback={<></>}>
        <TransitionModal
          view={authorizeModal.view}
          handleClose={() => {
            setAuthorizeModal({ view: false, invoice_id: 0, text: '' });
          }}
          bodyModal={tFormKey('ask-invoice-paid1').concat(' ', authorizeModal.text, ' ', tFormKey('ask-invoice-paid2'))}
          buttonOk={tKey('Sí')}
          buttonKo={tKey('No')}
          title={tFormKey('Marcar como pagada')}
          handleYes={() => {
            markAsPaid(authorizeModal.invoice_id);
            setAuthorizeModal({ view: false, invoice_id: 0, text: '' });
          }}
        />
      </Suspense>
    </Layout>
  );
};

const mapStateToProps = (state: AppState) => ({
  accountingLoading: selectLoading(state),
  can_pay: selectUserCan(state)('allow_send_invoice_pending_to_sage'),
  can_mark_as_paid: selectUserCan(state)('allow_mark_invoice_as_paid'),
});

const mapDispatchToProps = (dispatch: any) => ({
  markAsPaid: (invoice_id: number) => dispatch(markInvoiceAsPaid(invoice_id)),
  payInvoices: () => dispatch(payInvoices()),
  showSnackbar: (message: string, severity: SeveritySnackbar): void => dispatch(showSnackbar(message, severity)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AuthorizedInvoicesList);
